// Tandem section manager — Systems Quadrad for desktop Tandem (no workspace app volume).
// IPC for tandem lives on window.tandemAPI (revolt-desktop TandemManager).

import { Manager, logger } from "../../../core";
import { TandemToolSystem } from "../../../systems/core/instances/tandem/base/TandemToolSystem.js";
import { TandemAIToolSystem } from "../../../systems/core/instances/tandem/base/TandemAIToolSystem.js";
import { TandemAIInferenceSystem } from "../../../systems/core/instances/tandem/base/TandemAIInferenceSystem.js";
import { BasicTandemAIToolSet } from "../../../systems/core/instances/tandem/implementations/ai/BasicTandemAIToolSet.js";
import { BasicTandemEngineerAIToolSet } from "../../../systems/core/instances/tandem/implementations/ai/basicTandemEngineerAIToolSet.js";
import {
  TandemBubbleUiSystem,
  TANDEM_LANES,
  TANDEM_SPEAKERS,
} from "../../../systems/core/instances/tandem/base/TandemBubbleUiSystem.js";
import { connectInstanceSystems } from "../../../systems/core/utils/connectInstanceSystems.js";
import { bindCheckpointRestoreToChatArea } from "../../../systems/core/utils/checkpointRestoreBinder.js";
import { bindInferenceToChatArea } from "../../../systems/core/utils/inferenceChatBinder.js";
import { userMessageBubbleController } from "../../../systems/core/controllers/index.js";
import { compileEngineerTurnForInstructor } from "../utils/engineerTurnCompiler.js";
import { Chat } from "../../../systems/core/chat/Chat.js";
import { reconstructTandemMergedChatBubbles } from "../utils/tandemChatRestoration.js";

export class TandemManager extends Manager {
  constructor() {
    super();

    this.tandemToolSystem = new TandemToolSystem();
    this.tandemAIToolSystem = new TandemAIToolSystem();
    this.tandemAIInferenceSystem = new TandemAIInferenceSystem();
    this.tandemBubbleUiSystem = new TandemBubbleUiSystem();

    connectInstanceSystems({
      toolSystem: this.tandemToolSystem,
      aiToolSystem: this.tandemAIToolSystem,
      aiInferenceSystem: this.tandemAIInferenceSystem,
      bubbleUiSystem: this.tandemBubbleUiSystem,
      manager: this,
    });

    const basicTandemAIToolSet = new BasicTandemAIToolSet();
    this.tandemAIToolSystem.registerToolSet(basicTandemAIToolSet);

    const tandemEngineerAIToolSet = new BasicTandemEngineerAIToolSet();
    this.tandemAIToolSystem.registerToolSet(tandemEngineerAIToolSet);

    /** @type {import("../../../systems/core/ui/BubbleUiSystem.js").BubbleUiSystem | null} */
    this.bubbleUiSystem = null;
    /** @type {import("../../../systems/core/ui/BubbleUiSystem.js").BubbleUiSystem | null} */
    this.chatArea = null;
    /** @type {Record<string, unknown> | null} */
    this.aiChatInterface = null;

    this.isGenerating = false;
    this.activeInferenceMode = null;
    this._inferenceBinderCleanup = null;

    this.userMessageLastBubbleIdByMessageId = new Map();
    this.responseRootMessageIdByResponseMessageId = new Map();
    this.userMessageBubbleIdsByMessageId = new Map();

    /** Set by `cancelGeneration()` so a multi-phase user turn can stop before phase 2. */
    this._tandemRunCancelled = false;

    /** When non-null, `onInferenceFinished` may advance instructor ↔ engineer orchestration. */
    this._tandemLoopContext = null;
    /** Completed `tandem-prompt-*` tool calls for the wave that just finished (instructor lane). */
    this._tandemOrchestrationWave = [];
    this._unbindTandemOrchestrationListeners = null;
    this._onTandemToolCallCompleted = this._onTandemToolCallCompleted.bind(this);

    /**
     * Absolute native path for Tandem Tool-system file ops (user-selected folder).
     * Synced from the section UI via {@link setWorkingDirectoryPath}.
     * @type {string}
     */
    this.workingDirectoryPath = "";

    /** Revolt Desktop project row id (from `tandem-projects.json`). */
    this.activeProjectId = "";
    /** Logical Tandem session id (row under `project.chats[]`). */
    this.activeTandemChatSessionId = "";
    /** Avoid double bootstrap when React re-runs effects for the same project. */
    this._bootstrappedForProjectId = null;
    /** @type {ReturnType<typeof setTimeout> | null} */
    this._tandemPersistTimer = null;
  }

  getTandemDesktopApi() {
    if (typeof window === "undefined") return null;
    return window.tandemAPI || null;
  }

  clearTandemProjectContext() {
    this.activeProjectId = "";
    this.activeTandemChatSessionId = "";
    this._bootstrappedForProjectId = null;
    if (this._tandemPersistTimer) {
      clearTimeout(this._tandemPersistTimer);
      this._tandemPersistTimer = null;
    }
  }

  setActiveProjectContext(projectId) {
    const next =
      typeof projectId === "string" && projectId.trim() ? projectId.trim() : "";
    this.activeProjectId = next;
    if (!next) {
      this.activeTandemChatSessionId = "";
    }
  }

  _countNonSystemMessages(chat) {
    if (!chat?.messages || !Array.isArray(chat.messageIDs)) return 0;
    let n = 0;
    for (const id of chat.messageIDs) {
      const m = chat.messages.get(id);
      const role = m?.data?.role;
      if (role && role !== "system") n += 1;
    }
    return n;
  }

  shouldPersistTandemChats() {
    if (!this.activeProjectId || !this.activeTandemChatSessionId) return false;
    const inst = this.tandemAIInferenceSystem?.chatInstructor;
    const eng = this.tandemAIInferenceSystem?.chatEngineer;
    return this._countNonSystemMessages(inst) + this._countNonSystemMessages(eng) > 0;
  }

  schedulePersistTandemSession() {
    if (!this.shouldPersistTandemChats()) return;
    if (this._tandemPersistTimer) {
      clearTimeout(this._tandemPersistTimer);
    }
    this._tandemPersistTimer = setTimeout(() => {
      this._tandemPersistTimer = null;
      void this._flushPersistTandemSession();
    }, 450);
  }

  async _flushPersistTandemSession() {
    if (!this.shouldPersistTandemChats()) return;
    const api = this.getTandemDesktopApi();
    if (!api || typeof api.upsertTandemChat !== "function") return;
    const inst = this.tandemAIInferenceSystem.chatInstructor;
    const eng = this.tandemAIInferenceSystem.chatEngineer;
    const name =
      typeof inst?.getName === "function" ? inst.getName() : "Tandem chat";
    try {
      const res = await api.upsertTandemChat({
        projectId: this.activeProjectId,
        id: this.activeTandemChatSessionId,
        name,
        instructorChat: inst.toJSON(),
        engineerChat: eng.toJSON(),
      });
      if (!res?.success) {
        logger.warn(
          { tags: "manager|tandem|persist", color1: "yellow" },
          "upsertTandemChat:",
          res?.error
        );
      }
    } catch (err) {
      logger.error(
        { tags: "manager|tandem|persist", color1: "red" },
        "upsertTandemChat failed:",
        err
      );
    }
  }

  async listTandemChatSummaries() {
    const api = this.getTandemDesktopApi();
    if (!api?.listTandemChats || !this.activeProjectId) return [];
    const res = await api.listTandemChats({ projectId: this.activeProjectId });
    if (!res?.success || !Array.isArray(res.chats)) return [];
    return res.chats;
  }

  async loadTandemChatSession(chatId) {
    const api = this.getTandemDesktopApi();
    if (!api?.getTandemChat || !this.activeProjectId || !chatId) return;
    const res = await api.getTandemChat({
      projectId: this.activeProjectId,
      chatId,
    });
    if (!res?.success || !res.chat) {
      logger.warn(
        { tags: "manager|tandem|load", color1: "yellow" },
        "getTandemChat failed:",
        res?.error
      );
      this.startFreshTandemChatSession(this.chatArea);
      return;
    }
    const payload = res.chat;
    const inst = new Chat();
    const eng = new Chat();
    try {
      inst.fromJSON(payload.instructorChat);
      eng.fromJSON(payload.engineerChat);
    } catch (e) {
      logger.error(
        { tags: "manager|tandem|load", color1: "red" },
        "fromJSON failed:",
        e
      );
      this.startFreshTandemChatSession(this.chatArea);
      return;
    }
    this.tandemAIInferenceSystem.chatInstructor = inst;
    this.tandemAIInferenceSystem.chatEngineer = eng;
    this.tandemAIInferenceSystem.setActiveLane(TANDEM_LANES.ENGINEER);
    this.tandemAIInferenceSystem.chat = this.tandemAIInferenceSystem.chatEngineer;
    this.activeTandemChatSessionId = payload.id;

    reconstructTandemMergedChatBubbles({
      chatInstructor: inst,
      chatEngineer: eng,
      chatArea: this.chatArea,
      aiToolSystem: this.tandemAIToolSystem,
      tandemBubbleUiSystem: this.tandemBubbleUiSystem,
      onLinkUserMessageBubbles: this._linkUserMessageBubbles.bind(this),
    });

    try {
      const bubbleIds = this.chatArea?.getBubbles?.() || [];
      if (
        Array.isArray(bubbleIds) &&
        bubbleIds.length > 0 &&
        typeof this.chatArea?.scrollToBottomInstant === "function"
      ) {
        this.chatArea.scrollToBottomInstant();
      }
    } catch (_) {
      /* ignore */
    }
  }

  async deleteTandemChatById(chatId) {
    const api = this.getTandemDesktopApi();
    if (!api?.deleteTandemChat || !this.activeProjectId || !chatId) return false;
    const res = await api.deleteTandemChat({
      projectId: this.activeProjectId,
      chatId,
    });
    return !!res?.success;
  }

  async renameTandemChatById(chatId, name) {
    const api = this.getTandemDesktopApi();
    if (!api?.renameTandemChat || !this.activeProjectId || !chatId) return false;
    const res = await api.renameTandemChat({
      projectId: this.activeProjectId,
      chatId,
      name,
    });
    if (res?.success && this.tandemAIInferenceSystem?.chatInstructor?.setName) {
      this.tandemAIInferenceSystem.chatInstructor.setName(name);
    }
    return !!res?.success;
  }

  /**
   * @param {string} projectId
   * @param {import("../../../systems/core/instances/tandem/base/TandemBubbleUiSystem.js").TandemBubbleUiSystem | null} [bubbleSurface]
   */
  async bootstrapTandemChatForProject(projectId, bubbleSurface = null) {
    const pid = typeof projectId === "string" ? projectId.trim() : "";
    if (!pid) return;
    this.setActiveProjectContext(pid);
    if (this._bootstrappedForProjectId === pid) {
      return;
    }
    const surface = bubbleSurface || this.chatArea;
    const api = this.getTandemDesktopApi();
    if (!api?.listTandemChats || !api?.getTandemChat) {
      this.startFreshTandemChatSession(surface);
      this._bootstrappedForProjectId = pid;
      return;
    }
    try {
      const listRes = await api.listTandemChats({ projectId: pid });
      if (listRes?.success && Array.isArray(listRes.chats) && listRes.chats.length > 0) {
        await this.loadTandemChatSession(listRes.chats[0].id);
      } else {
        this.startFreshTandemChatSession(surface);
      }
    } catch (err) {
      logger.error(
        { tags: "manager|tandem|bootstrap", color1: "red" },
        "bootstrap failed:",
        err
      );
      this.startFreshTandemChatSession(surface);
    }
    this._bootstrappedForProjectId = pid;
  }

  startFreshTandemChatSession(bubbleSurface) {
    this._tandemRunCancelled = false;
    const sid =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `tandem-${Date.now()}`;
    this.activeTandemChatSessionId = sid;
    this._clearTandemLoop();
    const surface = bubbleSurface || this.chatArea;
    if (surface?.clearBubbles) {
      surface.clearBubbles();
    }
    this.userMessageLastBubbleIdByMessageId.clear();
    this.responseRootMessageIdByResponseMessageId.clear();
    this.userMessageBubbleIdsByMessageId.clear();
    this.tandemAIInferenceSystem.newChat();
    if (this.aiChatInterface?.setInputValue) {
      this.aiChatInterface.setInputValue("");
    }
    this._setGeneratingState(false);
    if (this.aiChatInterface?.setInferenceError) {
      this.aiChatInterface.setInferenceError(null);
    }
  }

  /**
   * @param {string} dirPath - native folder path from the working-directory step
   */
  setWorkingDirectoryPath(dirPath) {
    this.workingDirectoryPath =
      typeof dirPath === "string" && dirPath.trim().length > 0
        ? dirPath.trim()
        : "";
  }

  async initStates() {
    logger.log(
      { tags: "manager|tandem|init", color1: "cyan" },
      "Tandem quadrad wired (file tools + tandemAPI)"
    );
  }

  cleanup() {
    try {
      this._inferenceBinderCleanup?.();
    } catch (_) {
      /* ignore */
    }
    this._inferenceBinderCleanup = null;
    try {
      this._unbindTandemOrchestrationListeners?.();
    } catch (_) {
      /* ignore */
    }
    this._unbindTandemOrchestrationListeners = null;
  }

  /**
   * @param {Record<string, unknown>} aiChatInterface
   * @param {import("../../../systems/core/ui/BubbleUiSystem.js").BubbleUiSystem} bubbleUiSystem
   */
  registerUIReferences(aiChatInterface, bubbleUiSystem) {
    logger.log(
      { tags: "manager|tandem|ui-refs", color1: "blue" },
      "Registering Tandem UI references",
      {
        hasAIChatInterface: !!aiChatInterface,
        hasBubbleUi: !!bubbleUiSystem,
      }
    );

    this.aiChatInterface = aiChatInterface;
    this.bubbleUiSystem = bubbleUiSystem;
    this.chatArea = bubbleUiSystem;

    this.tandemBubbleUiSystem?.setSystemContext?.({
      workspaceManager: this,
      toolSystem: this.tandemToolSystem,
      aiToolSystem: this.tandemAIToolSystem,
      aiInferenceSystem: this.tandemAIInferenceSystem,
      aiChatInterface,
    });

    this.checkpointRestoreBinder = bindCheckpointRestoreToChatArea({
      chat: () => this.tandemAIInferenceSystem?.chat,
      aiInferenceSystem: this.tandemAIInferenceSystem,
      chatArea: this.chatArea,
      options: {
        userMessageLastBubbleIdByMessageId: () =>
          this.userMessageLastBubbleIdByMessageId,
        responseRootMessageIdByResponseMessageId: () =>
          this.responseRootMessageIdByResponseMessageId,
        logTagPrefix: "manager|tandem|restore",
      },
    });

    this._setupInferenceEventListeners();
  }

  _setGeneratingState(isGenerating) {
    this.isGenerating = isGenerating;
    if (this.aiChatInterface?.setIsGenerating) {
      this.aiChatInterface.setIsGenerating(isGenerating);
    }
  }

  _setupInferenceEventListeners() {
    if (typeof this.tandemAIInferenceSystem.removeAllListeners === "function") {
      this.tandemAIInferenceSystem.removeAllListeners();
    }

    try {
      this._inferenceBinderCleanup?.();
    } catch (_) {
      /* ignore */
    }

    try {
      this._unbindTandemOrchestrationListeners?.();
    } catch (_) {
      /* ignore */
    }
    this.tandemAIInferenceSystem.addEventListener(
      "onToolCallCompleted",
      this._onTandemToolCallCompleted
    );
    this._unbindTandemOrchestrationListeners = () => {
      this.tandemAIInferenceSystem.removeEventListener(
        "onToolCallCompleted",
        this._onTandemToolCallCompleted
      );
    };

    const { cleanup } = bindInferenceToChatArea({
      aiInferenceSystem: this.tandemAIInferenceSystem,
      aiToolSystem: this.tandemAIToolSystem,
      chatArea: this.chatArea,
      options: {
        logTagPrefix: "manager|tandem|inference",
        persistToolCallToSegment: false,
        onInferenceFinished: (messageId, data) => {
          this._setGeneratingState(false);
          if (data?.rootResponseMessageId) {
            this.responseRootMessageIdByResponseMessageId.set(
              messageId,
              data.rootResponseMessageId
            );
          }
          this.checkpointRestoreBinder?.attachRestoreControlForResponse(
            messageId,
            data?.rootResponseMessageId || null
          );
          void this._advanceTandemOrchestrationAfterInfer(messageId, data).catch((error) => {
            logger.error(
              { tags: "manager|tandem|orchestration", color1: "red" },
              "Tandem orchestration advance failed:",
              error
            );
            this._clearTandemLoop();
            if (this.aiChatInterface?.setInferenceError) {
              this.aiChatInterface.setInferenceError(
                error instanceof Error ? error : new Error(String(error?.message || error))
              );
            }
          });
        },
        onError: (messageId, error) => {
          if (this.aiChatInterface?.setInferenceError) {
            this.aiChatInterface.setInferenceError(error);
          }
        },
        onComplete: () => {},
      },
    });

    const binderCleanup = cleanup;
    this._inferenceBinderCleanup = () => {
      try {
        binderCleanup();
      } finally {
        try {
          this._unbindTandemOrchestrationListeners?.();
        } catch (_) {
          /* ignore */
        }
        this._unbindTandemOrchestrationListeners = null;
      }
    };
  }

  cancelGeneration() {
    logger.log(
      { tags: "manager|tandem|cancel", color1: "yellow" },
      "cancelGeneration — stopping tandem inference"
    );

    this._tandemRunCancelled = true;

    if (
      this.activeInferenceMode === "local" &&
      typeof window !== "undefined" &&
      window.llamaCppAPI?.cancelGenerate
    ) {
      try {
        window.llamaCppAPI.cancelGenerate();
      } catch (e) {
        logger.warn(
          { tags: "manager|tandem|cancel|local", color1: "yellow" },
          "llamaCppAPI.cancelGenerate failed:",
          e
        );
      }
    }

    if (this.activeInferenceMode === "cloud") {
      void this._cancelActiveRequests();
    }

    this.tandemAIInferenceSystem?.cancel?.();
  }

  async _cancelActiveRequests() {
    try {
      const activeContexts = Array.from(
        this.tandemAIInferenceSystem?.activeContexts || []
      );

      if (activeContexts.length === 0) {
        return;
      }

      for (const context of activeContexts) {
        if (!context.requestId) continue;
        try {
          const inferenceSessionId = context.inferenceSessionId || null;
          const response = await fetch("/api/cancel-request", {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              requestId: context.requestId,
              inferenceSessionId,
            }),
          });
          if (!response.ok) {
            const responseText = await response.text().catch(() => "");
            logger.warn(
              { tags: "manager|tandem|cancel|signal", color1: "yellow" },
              "Cancel request not OK",
              { status: response.status, body: responseText?.slice(0, 200) }
            );
          }
        } catch (error) {
          logger.error(
            { tags: "manager|tandem|cancel|signal", color1: "red" },
            "Cancellation signal failed:",
            error?.message
          );
        }
      }
    } catch (error) {
      logger.error(
        { tags: "manager|tandem|cancel|signal", color1: "red" },
        "Failed to cancel active requests:",
        error?.message
      );
    }
  }

  async _convertFileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  async _processMessageAttachments({ userMessage, attachments = [] }) {
    const images = [];
    const files = [];
    for (const attachment of attachments) {
      if (attachment.type === "image") {
        images.push(attachment);
      } else {
        files.push(attachment);
      }
    }
    return {
      userMessage: userMessage || "",
      images,
      files,
    };
  }

  async _createUserMessageBubbles({ processedMessage, chatArea }) {
    const bubbleSurface = chatArea || this.chatArea;
    if (!bubbleSurface?.createBubble) {
      return { bubbleIds: [], lastBubbleId: null };
    }

    const currentTimestamp = new Date();
    const imageContents = await Promise.all(
      processedMessage.images.map(async (imageAtt) =>
        this._convertFileToBase64(imageAtt.file)
      )
    );
    const fileContents = await Promise.all(
      processedMessage.files.map(async (fileAtt) =>
        this._convertFileToBase64(fileAtt.file)
      )
    );
    const fileNames = processedMessage.files.map((att) => att.name);

    const bubbleConfigs = userMessageBubbleController.createMessageBubbles({
      text: processedMessage.userMessage.trim(),
      images: imageContents,
      files: fileContents,
      fileNames,
      timestamp: currentTimestamp,
    });

    const bubbleIds = [];
    let lastBubbleId = null;
    bubbleConfigs.forEach((bubbleConfig) => {
      const bubbleId = bubbleSurface.createBubble(bubbleConfig);
      if (bubbleId) {
        bubbleIds.push(bubbleId);
        lastBubbleId = bubbleId;
      }
    });

    return { bubbleIds, lastBubbleId };
  }

  _linkUserMessageBubbles({ messageId, bubbleIds = [], lastBubbleId = null }) {
    if (!messageId || bubbleIds.length === 0) {
      return;
    }
    this.userMessageBubbleIdsByMessageId.set(messageId, bubbleIds);
    if (lastBubbleId) {
      this.userMessageLastBubbleIdByMessageId.set(messageId, lastBubbleId);
    }
    if (!this.chatArea?.updateBubble) {
      return;
    }
    bubbleIds.forEach((bubbleId) => {
      this.chatArea.updateBubble(bubbleId, {
        messageId,
        userMessageId: messageId,
        isUserMessage: true,
      });
    });
  }

  async _addUserMessageToChat({ processedMessage }) {
    const messageData = {
      role: "user",
      segments: [],
    };

    const stampSegment = (seg) => {
      const now = new Date();
      return {
        ...seg,
        startTimestamp: seg.startTimestamp || now,
        endTimestamp: seg.endTimestamp || now,
      };
    };

    if (processedMessage.userMessage.trim()) {
      messageData.segments.push(
        stampSegment({
          type: "text",
          content: processedMessage.userMessage.trim(),
        })
      );
    }

    if (processedMessage.images?.length > 0) {
      const imagePromises = processedMessage.images.map(async (image) => {
        const base64Content = await this._convertFileToBase64(image.file);
        return stampSegment({
          type: "image",
          content: base64Content,
          mimeType: image.type || "image/jpeg",
          fileName: image.name,
        });
      });
      messageData.segments.push(...(await Promise.all(imagePromises)));
    }

    if (processedMessage.files?.length > 0) {
      const filePromises = processedMessage.files.map(async (file) => {
        const base64Content = await this._convertFileToBase64(file.file);
        return stampSegment({
          type: "file",
          content: base64Content,
          mimeType: file.type || "application/octet-stream",
          fileName: file.name,
        });
      });
      messageData.segments.push(...(await Promise.all(filePromises)));
    }

    return this.tandemAIInferenceSystem.addMessage(messageData);
  }

  async _ensureLocalModelLoadedForInference({
    modelId,
    requestOptions = {},
  }) {
    const { localModelLoadOptions = null } = requestOptions || {};
    const api = typeof window !== "undefined" ? window.llamaCppAPI || null : null;
    const selectedLocalModelPath = this.aiChatInterface?.selectedModel?.path || null;

    if (!api?.loadModel || !api?.unloadModel || !api?.isModelLoaded) {
      throw new Error(
        "Local inference requires the Revolt Desktop llamaCpp API (window.llamaCppAPI)."
      );
    }

    if (!selectedLocalModelPath || typeof selectedLocalModelPath !== "string") {
      throw new Error("Local inference requires a selected local model with a valid path.");
    }

    const setLocalUI = (patch) => {
      if (patch?.loadState && this.aiChatInterface?.setLocalModelLoadState) {
        this.aiChatInterface.setLocalModelLoadState(patch.loadState);
      }
      if (patch?.progress != null && this.aiChatInterface?.setLocalModelLoadProgress) {
        this.aiChatInterface.setLocalModelLoadProgress(patch.progress);
      }
      if (patch?.statusMessage !== undefined && this.aiChatInterface?.setLocalModelStatusMessage) {
        this.aiChatInterface.setLocalModelStatusMessage(patch.statusMessage);
      }
      if (patch?.statusType !== undefined && this.aiChatInterface?.setLocalModelStatusType) {
        this.aiChatInterface.setLocalModelStatusType(patch.statusType);
      }
      if (patch?.metrics !== undefined && this.aiChatInterface?.setLocalContextMetrics) {
        this.aiChatInterface.setLocalContextMetrics(patch.metrics);
      }
    };

    const loaded = await api.isModelLoaded();
    const info = loaded?.modelInfo || null;
    const isLoaded = loaded?.isLoaded === true;
    const matchesPath = !!info?.modelPath && info.modelPath === selectedLocalModelPath;

    if (isLoaded && matchesPath) {
      setLocalUI({
        loadState: "loaded",
        progress: 0,
        statusMessage: "Model ready",
        statusType: "success",
        metrics: {
          runtime: info?.contextSize ?? null,
          train: info?.trainContextSize ?? null,
          gpuLayers: info?.gpuLayers ?? null,
          totalLayers: info?.totalLayers ?? null,
        },
      });
      return;
    }

    let cleanupProgress = null;
    if (api?.onLoadProgress && this.aiChatInterface?.setLocalModelLoadProgress) {
      cleanupProgress = api.onLoadProgress((payload) => {
        const p = typeof payload === "number" ? payload : payload?.progress ?? 0;
        const percent = p <= 1 ? Math.round(p * 100) : Math.round(p);
        setLocalUI({ progress: Math.min(100, Math.max(0, percent)) });
      });
    }

    setLocalUI({
      loadState: "loading",
      progress: 0,
      statusMessage: "Loading model…",
      statusType: "info",
      metrics: null,
    });

    try {
      const opts = localModelLoadOptions || {};
      const contextSize =
        opts.contextSizeMode === "manual" &&
        opts.contextSizeMin != null &&
        opts.contextSizeMax != null
          ? {
              min: Math.max(512, parseInt(opts.contextSizeMin, 10) || 2048),
              max: Math.min(128000, parseInt(opts.contextSizeMax, 10) || 4096),
            }
          : "auto";

      const contextAllocationSize =
        opts.contextAllocationMode === "manual" && opts.contextAllocationSize != null
          ? parseInt(opts.contextAllocationSize, 10) || undefined
          : undefined;

      const defaultContextFlashAttention = !!opts.defaultContextFlashAttention;

      await api.loadModel(selectedLocalModelPath, {
        contextSize,
        contextAllocationSize,
        defaultContextFlashAttention,
      });

      const postLoaded = await api.isModelLoaded();
      const postInfo = postLoaded?.modelInfo || null;
      const postIsLoaded = postLoaded?.isLoaded === true;
      const postMatchesPath =
        !!postInfo?.modelPath && postInfo.modelPath === selectedLocalModelPath;

      if (postIsLoaded && postMatchesPath) {
        setLocalUI({
          loadState: "loaded",
          progress: 0,
          statusMessage: "Model ready",
          statusType: "success",
          metrics: {
            runtime: postInfo?.contextSize ?? null,
            train: postInfo?.trainContextSize ?? null,
            gpuLayers: postInfo?.gpuLayers ?? null,
            totalLayers: postInfo?.totalLayers ?? null,
          },
        });
      } else {
        setLocalUI({
          loadState: "error",
          progress: 0,
          statusMessage: postLoaded?.error || "Load failed",
          statusType: "error",
          metrics: null,
        });
        throw new Error(postLoaded?.error || "Local model failed to load");
      }
    } finally {
      if (typeof cleanupProgress === "function") {
        try {
          cleanupProgress();
        } catch (_) {
          /* ignore */
        }
      }
    }
  }

  async _performInference({ modelId, userId, requestOptions = {}, inferenceMode = "cloud" }) {
    // Strip desktop-only load knobs before merging into OpenRouter payload (local path uses full `requestOptions` in `_ensureLocalModelLoadedForInference`).
    const { localModelLoadOptions: _omitLocalKnobs, ...openRouterRequestOptions } =
      requestOptions || {};
    void _omitLocalKnobs;

    const defaultRequestOptions = {
      max_tokens: 25000,
      repetition_penalty: 1.12,
      frequency_penalty: 0.6,
      presence_penalty: 0.3,
      reasoning: { effort: "low" },
      ...openRouterRequestOptions,
    };

    if (inferenceMode === "local") {
      await this._ensureLocalModelLoadedForInference({
        modelId,
        requestOptions,
      });
    }

    this.activeInferenceMode = inferenceMode;

    try {
      return await this.tandemAIInferenceSystem.infer(modelId, userId, null, {
        requestOptions: defaultRequestOptions,
        mode: inferenceMode,
        instanceContext: null,
      });
    } finally {
      this.activeInferenceMode = null;
    }
  }

  _clearTandemLoop() {
    this._tandemLoopContext = null;
    this._tandemOrchestrationWave = [];
  }

  _onTandemToolCallCompleted(_messageId, toolCall, _modelFacing) {
    if (!this._tandemLoopContext) {
      return;
    }
    const name = toolCall?.function?.name;
    if (name !== "tandem-prompt-engineer") {
      return;
    }
    let parsedArgs = toolCall?.function?.arguments;
    if (typeof parsedArgs === "string") {
      try {
        parsedArgs = JSON.parse(parsedArgs);
      } catch {
        parsedArgs = {};
      }
    }
    this._tandemOrchestrationWave.push({
      toolName: name,
      parsedArgs: parsedArgs && typeof parsedArgs === "object" ? parsedArgs : {},
    });
  }

  async _advanceTandemOrchestrationAfterInfer(_messageId, data) {
    if (data?.wasCancelled || this._tandemRunCancelled) {
      this._clearTandemLoop();
      return;
    }
    if (data?.error || data?.abnormalTermination) {
      this._clearTandemLoop();
      return;
    }
    if (!this._tandemLoopContext) {
      return;
    }

    const { bubbleSurface, modelId, userId, requestOptions, inferenceMode } =
      this._tandemLoopContext;
    const lane = this.tandemAIInferenceSystem.getActiveLane();

    if (lane === TANDEM_LANES.ENGINEER) {
      const compiled = compileEngineerTurnForInstructor(
        this.tandemAIInferenceSystem.chatEngineer
      );
      this.setActiveLane(TANDEM_LANES.INSTRUCTOR, {
        userSpeaker: TANDEM_SPEAKERS.ENGINEER,
      });
      this._tandemOrchestrationWave = [];
      await this._runSingleLaneUserInference({
        processedMessage: { userMessage: compiled, images: [], files: [] },
        bubbleSurface,
        modelId,
        userId,
        requestOptions,
        inferenceMode,
      });
      return;
    }

    if (lane !== TANDEM_LANES.INSTRUCTOR) {
      return;
    }

    const wave = this._tandemOrchestrationWave;
    this._tandemOrchestrationWave = [];

    const engEntries = wave.filter((w) => w.toolName === "tandem-prompt-engineer");
    const pickedEng = engEntries.length ? engEntries[engEntries.length - 1] : null;

    if (pickedEng) {
      const raw =
        (pickedEng.parsedArgs &&
          typeof pickedEng.parsedArgs.instructionsForEngineer === "string" &&
          pickedEng.parsedArgs.instructionsForEngineer.trim()) ||
        "";
      const handoff = {
        userMessage: raw
          ? `[Instructor]\n\n${raw}`
          : "[Instructor]\n\n(No instructions provided.)",
        images: [],
        files: [],
      };
      this.setActiveLane(TANDEM_LANES.ENGINEER, {
        userSpeaker: TANDEM_SPEAKERS.INSTRUCTOR,
      });
      this._tandemOrchestrationWave = [];
      await this._runSingleLaneUserInference({
        processedMessage: handoff,
        bubbleSurface,
        modelId,
        userId,
        requestOptions,
        inferenceMode,
      });
      return;
    }

    logger.log(
      { tags: "manager|tandem|orchestration", color1: "blue" },
      "Instructor finished without tandem-prompt-engineer — ending orchestration wave (human can send another message to continue)."
    );
    this._clearTandemLoop();
  }

  _shouldAbortTandemRun(inferContext) {
    return !!(inferContext?.cancelled || this._tandemRunCancelled);
  }

  /**
   * One user-authored message on the active lane + one `infer` call.
   */
  async _runSingleLaneUserInference({
    processedMessage,
    bubbleSurface,
    modelId,
    userId,
    requestOptions,
    inferenceMode,
  }) {
    const { bubbleIds, lastBubbleId } = await this._createUserMessageBubbles({
      processedMessage,
      chatArea: bubbleSurface,
    });

    if (bubbleSurface?.scrollToBottomAndEnable) {
      bubbleSurface.scrollToBottomAndEnable();
    }

    const messageId = await this._addUserMessageToChat({ processedMessage });
    this._linkUserMessageBubbles({
      messageId,
      bubbleIds,
      lastBubbleId,
    });

    this._setGeneratingState(true);
    try {
      return await this._performInference({
        modelId,
        userId,
        requestOptions,
        inferenceMode,
      });
    } catch (error) {
      this._setGeneratingState(false);
      throw error;
    }
  }

  /**
   * @param {Object} input
   * @param {string} input.userMessage
   * @param {Array} [input.attachments]
   * @param {string} input.modelId
   * @param {string|null} input.userId
   * @param {Record<string, unknown>} input.aiChatInterface
   * @param {import("../../../systems/core/ui/BubbleUiSystem.js").BubbleUiSystem} [input.bubbleUiSystem]
   * @param {Object} [input.requestOptions]
   * @param {"cloud"|"local"} [input.inferenceMode]
   * @param {"full"|"resume"|"single"} [input.tandemHandoffPolicy] — `full` starts instructor-led
   *   orchestration (`[user]` prefix + lane loop). `resume` / `single` run one `infer` on `lane`.
   * @param {"instructor"|"engineer"} [input.lane] — active lane when `tandemHandoffPolicy` is not `full`.
   */
  async sendMessage({
    userMessage,
    attachments = [],
    modelId,
    userId,
    aiChatInterface,
    bubbleUiSystem = null,
    requestOptions = {},
    inferenceMode = "cloud",
    tandemHandoffPolicy = "single",
    lane = TANDEM_LANES.INSTRUCTOR,
    userSpeaker = TANDEM_SPEAKERS.USER,
  }) {
    const bubbleSurface = bubbleUiSystem || this.chatArea;
    const ui = aiChatInterface || this.aiChatInterface;

    this._tandemRunCancelled = false;

    if (tandemHandoffPolicy === "full") {
      this._tandemOrchestrationWave = [];
      this._tandemLoopContext = {
        bubbleSurface,
        modelId,
        userId,
        requestOptions,
        inferenceMode,
      };
      this.setActiveLane(TANDEM_LANES.INSTRUCTOR, { userSpeaker: TANDEM_SPEAKERS.USER });
    } else {
      this._clearTandemLoop();
      this.setActiveLane(lane, { userSpeaker });
    }

    if (ui?.setInferenceError) {
      ui.setInferenceError(null);
    }

    if (!modelId) {
      throw new Error("No model selected");
    }

    let processedMessage = await this._processMessageAttachments({
      userMessage,
      attachments,
    });

    if (tandemHandoffPolicy === "full") {
      processedMessage = {
        ...processedMessage,
        userMessage: `[user]\n\n${processedMessage.userMessage}`,
      };
    }

    await this._runSingleLaneUserInference({
      processedMessage,
      bubbleSurface,
      modelId,
      userId,
      requestOptions,
      inferenceMode,
    });
    // Successful runs: bindInferenceToChatArea `onInferenceFinished` clears generating state.
  }

  /**
   * Switch which tandem lane is "active" for both the inference system (swaps the
   * underlying Chat) and the bubble UI (so subsequent bubbles are stamped with
   * the right speaker/lane). Must be called while no inference is in-flight.
   *
   * @param {"instructor" | "engineer"} lane
   * @param {Object} [options]
   * @param {string} [options.userSpeaker] — override speaker id used for user-role
   *   bubbles created next (e.g. "instructor" when the instructor authors a
   *   message into the engineer's chat). Defaults to the real user.
   */
  setActiveLane(lane, { userSpeaker } = {}) {
    if (lane !== TANDEM_LANES.INSTRUCTOR && lane !== TANDEM_LANES.ENGINEER) {
      return;
    }

    this.tandemAIInferenceSystem?.setActiveLane?.(lane);
    this.tandemBubbleUiSystem?.setActiveLane?.(lane);

    const assistantSpeaker =
      lane === TANDEM_LANES.INSTRUCTOR
        ? TANDEM_SPEAKERS.INSTRUCTOR
        : TANDEM_SPEAKERS.ENGINEER;
    this.tandemBubbleUiSystem?.setActiveAssistantSpeaker?.(assistantSpeaker);

    const overrideUserSpeaker =
      userSpeaker && userSpeaker !== TANDEM_SPEAKERS.USER ? userSpeaker : null;
    this.tandemBubbleUiSystem?.setPendingUserSpeakerOverride?.(overrideUserSpeaker);
  }

  handleNewChat({ bubbleUiSystem } = {}) {
    const surface = bubbleUiSystem || this.chatArea;
    this.startFreshTandemChatSession(surface);
  }
}
